import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingStorefrontIcon, ArrowLeftOnRectangleIcon, ArrowPathIcon, PlusIcon } from '@heroicons/react/24/outline';
import { shopsApi } from '../../utils/api';
import { signOut } from '../../utils/supabase';
import { PAYS, DEVISES, deviseDuPays } from '../../constants/paysDevises';
import './ShopSelectionGate.css';

/**
 * Affiche une page autonome de choix de boutique avant l'accès au backoffice.
 * Le dernier choix reste enregistré dans le navigateur, mais n'est accepté
 * que s'il correspond toujours à une boutique accessible par l'utilisateur.
 */
const ShopSelectionGate = () => {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // Champs de création : nom, pays, devise puis coordonnées de la boutique
  const FORMULAIRE_VIDE = { name: '', country_code: 'CI', currency: 'XOF', whatsapp: '', phone: '', address: '' };
  const [createForm, setCreateForm] = useState(FORMULAIRE_VIDE);
  const [error, setError] = useState('');

  /**
   * Charge les boutiques auxquelles le compte est membre et retire une ancienne
   * sélection locale devenue invalide, sans choisir automatiquement à sa place.
   */
  const loadShops = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await shopsApi.getAll({ is_active: true });
      const availableShops = response.data || [];
      const savedShopId = window.localStorage.getItem('activeShopId');

      if (savedShopId && !availableShops.some((shop) => shop.id === savedShopId)) {
        window.localStorage.removeItem('activeShopId');
      }
      setShops(availableShops);
    } catch (loadError) {
      console.error('Erreur lors du chargement des boutiques accessibles:', loadError);
      setError('Impossible de charger vos boutiques. Réessayez dans un instant.');
    } finally {
      setIsLoading(false);
    }
  };

  /** Charge les choix disponibles à l'ouverture de la session. */
  useEffect(() => {
    loadShops();
  }, []);

  /**
   * Enregistre un choix explicite puis ouvre le tableau de bord. Comme cette
   * page est hors du dashboard, aucune donnée métier n'a été chargée avant.
   */
  const selectShop = (shopId) => {
    window.localStorage.setItem('activeShopId', shopId);
    window.dispatchEvent(new CustomEvent('active-shop-changed', { detail: { shopId } }));
    navigate('/app/dashboard', { replace: true });
  };

  /** Met à jour le formulaire court de création sans effacer les autres champs. */
  const handleCreateFieldChange = (event) => {
    const { name, value } = event.target;
    // Le choix d'un pays propose automatiquement sa devise, modifiable ensuite
    setCreateForm((previousForm) => ({
      ...previousForm,
      [name]: value,
      ...(name === 'country_code' && { currency: deviseDuPays(value, previousForm.currency) }),
    }));
    setError('');
  };

  /**
   * Crée une boutique depuis le même écran, puis recharge la liste afin que le
   * marchand choisisse explicitement la boutique à ouvrir.
   */
  const handleCreateShop = async (event) => {
    event.preventDefault();
    if (createForm.name.trim().length < 2) {
      setError('Indiquez un nom de boutique d’au moins 2 caractères.');
      return;
    }

    const phonePattern = /^\+?[0-9\s-]{8,20}$/;
    // Le WhatsApp est obligatoire : il est lié à la boutique et alimente son site vitrine
    if (!phonePattern.test(createForm.whatsapp.trim())) {
      setError('Indiquez un numéro WhatsApp au format international, par exemple +225 07 00 00 00 00.');
      return;
    }
    if (createForm.phone.trim() && !phonePattern.test(createForm.phone.trim())) {
      setError('Indiquez un numéro de téléphone valide au format international.');
      return;
    }

    try {
      setIsCreating(true);
      setError('');
      const name = createForm.name.trim();
      const whatsapp = createForm.whatsapp.trim();
      const phone = createForm.phone.trim();
      const address = createForm.address.trim();
      await shopsApi.create({
        name,
        country_code: createForm.country_code,
        currency: createForm.currency,
        phone: phone || whatsapp || null,
        // Transmis au serveur pour pré-remplir aussi le WhatsApp du site vitrine
        whatsapp: whatsapp || null,
        address: address || null,
        is_active: true,
      });

      setCreateForm(FORMULAIRE_VIDE);
      setIsCreateOpen(false);
      await loadShops();
    } catch (createError) {
      console.error('Erreur lors de la création de la boutique:', createError);
      setError(createError.response?.data?.error || 'La création de la boutique a échoué. Réessayez dans un instant.');
    } finally {
      setIsCreating(false);
    }
  };

  /** Déconnecte le compte depuis la page de choix, même si aucune boutique ne charge. */
  const handleSignOut = async () => {
    try {
      const { error: signOutError } = await signOut();
      if (signOutError) throw signOutError;
      navigate('/login', { replace: true });
    } catch (signOutError) {
      console.error('Erreur lors de la déconnexion depuis le choix de boutique:', signOutError);
      setError('La déconnexion a échoué. Réessayez dans un instant.');
    }
  };

  // La page reste volontairement visible même si une boutique est déjà active :
  // elle sert aussi à changer explicitement de contexte depuis une autre page.
  if (isLoading) return null;

  return (
    <main className="shop-selection-gate" aria-labelledby="shop-selection-title">
      <div className="shop-selection-gate__panel">
        <div className="shop-selection-gate__icon" aria-hidden="true"><BuildingStorefrontIcon /></div>
        <p className="shop-selection-gate__eyebrow">Espace marchand</p>
        <h1 id="shop-selection-title">Choisissez votre boutique</h1>
        <p className="shop-selection-gate__description">Vos produits, commandes, livreurs et réglages sont séparés pour chaque boutique.</p>

        {error && <p className="shop-selection-gate__error" role="alert">{error}</p>}

        {!isLoading && !error && shops.length === 0 && (
          <p className="shop-selection-gate__error" role="alert">Aucune boutique accessible n’a été trouvée pour ce compte.</p>
        )}

        <div className="shop-selection-gate__list" aria-label="Boutiques disponibles">
          {shops.map((shop) => (
            <button key={shop.id} className="shop-selection-gate__shop" type="button" onClick={() => selectShop(shop.id)}>
              <span className="shop-selection-gate__shop-name">{shop.name}</span>
              <span className="shop-selection-gate__shop-meta">Accéder à cette boutique</span>
            </button>
          ))}
        </div>

        {!isCreateOpen ? (
          <button className="shop-selection-gate__create" type="button" onClick={() => setIsCreateOpen(true)}>
            <PlusIcon aria-hidden="true" /> Créer une boutique
          </button>
        ) : (
          <form className="shop-selection-gate__form" onSubmit={handleCreateShop}>
            <h2>Créer une boutique</h2>
            <div className="shop-selection-gate__field">
              <label htmlFor="shop-create-name">Nom de la boutique</label>
              <input id="shop-create-name" name="name" autoComplete="organization" value={createForm.name} onChange={handleCreateFieldChange} required minLength="2" />
            </div>
            <div className="shop-selection-gate__form-grid">
              <div className="shop-selection-gate__field">
                <label htmlFor="shop-create-country">Pays</label>
                <select id="shop-create-country" name="country_code" value={createForm.country_code} onChange={handleCreateFieldChange}>
                  {PAYS.map((pays) => <option key={pays.code} value={pays.code}>{pays.label}</option>)}
                </select>
              </div>
              <div className="shop-selection-gate__field">
                <label htmlFor="shop-create-currency">Devise</label>
                <select id="shop-create-currency" name="currency" value={createForm.currency} onChange={handleCreateFieldChange}>
                  {DEVISES.map((devise) => <option key={devise.code} value={devise.code}>{devise.label}</option>)}
                </select>
              </div>
            </div>
            {/* Coordonnées de la boutique : le WhatsApp alimente aussi son site vitrine */}
            <div className="shop-selection-gate__field">
              <label htmlFor="shop-create-whatsapp">WhatsApp professionnel *</label>
              <input id="shop-create-whatsapp" name="whatsapp" type="tel" autoComplete="tel" placeholder="+225 07 00 00 00 00" value={createForm.whatsapp} onChange={handleCreateFieldChange} required />
            </div>
            <div className="shop-selection-gate__field">
              <label htmlFor="shop-create-phone">Téléphone (facultatif)</label>
              <input id="shop-create-phone" name="phone" type="tel" autoComplete="tel" placeholder="Si différent de WhatsApp" value={createForm.phone} onChange={handleCreateFieldChange} />
            </div>
            <div className="shop-selection-gate__field">
              <label htmlFor="shop-create-address">Adresse (facultatif)</label>
              <input id="shop-create-address" name="address" autoComplete="street-address" maxLength="300" value={createForm.address} onChange={handleCreateFieldChange} />
            </div>
            <div className="shop-selection-gate__form-actions">
              <button type="button" className="shop-selection-gate__cancel" onClick={() => setIsCreateOpen(false)}>Annuler</button>
              <button type="submit" className="shop-selection-gate__create" disabled={isCreating}>{isCreating ? 'Création…' : 'Créer puis choisir'}</button>
            </div>
          </form>
        )}

        {error && (
          <button className="shop-selection-gate__retry" type="button" onClick={loadShops}>
            <ArrowPathIcon aria-hidden="true" /> Réessayer
          </button>
        )}
        <button className="shop-selection-gate__sign-out" type="button" onClick={handleSignOut}>
          <ArrowLeftOnRectangleIcon aria-hidden="true" /> Se déconnecter
        </button>
      </div>
    </main>
  );
};

export default ShopSelectionGate;
